import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Loading } from 'vnetwork-ui'
import { MessageCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { formatFromUTC } from '@/utils/function'
import { getChatMessages } from '@/modules/Livechat/services'
import { ChatMessage } from '@/modules/Livechat/types/response'

interface LivechatConversationPreviewProps {
    roomId: string
}

/** Compact message bubble for preview */
const PreviewMessage: React.FC<{ message: ChatMessage }> = ({ message }) => {
    const isEmployee = message.sender === 'employee'
    const isSystem = message.sender === 'system'
    const isBot = message.sender === 'agent'

    if (isSystem) {
        return (
            <div className="flex justify-center my-1">
                <span className="text-[11px] text-gray-400 italic">
                    {message.content}
                </span>
            </div>
        )
    }

    const senderName =
        message.user?.name ||
        (isBot ? 'Bot' : isEmployee ? 'Support' : 'Customer')

    const content =
        message.type === 'image'
            ? '📷 Image'
            : message.type === 'file'
              ? `📎 ${message.fileName || 'File'}`
              : message.type === 'audio'
                ? '🎵 Audio'
                : message.type === 'video'
                  ? '🎬 Video'
                  : message.content

    return (
        <div
            className={`flex ${isEmployee ? 'justify-end' : 'justify-start'} mb-1.5`}
        >
            <div
                className={`max-w-[80%] px-3 py-1.5 rounded-lg text-sm ${
                    isEmployee
                        ? 'bg-blue-500 text-white rounded-tr-sm'
                        : isBot
                          ? 'bg-gray-100 text-gray-700 rounded-tl-sm'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm'
                }`}
            >
                <div
                    className={`text-[10px] font-medium mb-0.5 ${
                        isEmployee
                            ? 'text-blue-100'
                            : isBot
                              ? 'text-gray-400'
                              : 'text-gray-500'
                    }`}
                >
                    {senderName}
                </div>
                <p className="whitespace-pre-wrap break-words leading-snug">
                    {content}
                </p>
                <div
                    className={`text-[9px] mt-0.5 text-right ${
                        isEmployee ? 'text-blue-200' : 'text-gray-400'
                    }`}
                >
                    {formatFromUTC(message.createdAt, 'HH:mm')}
                </div>
            </div>
        </div>
    )
}

const LivechatConversationPreview: React.FC<
    LivechatConversationPreviewProps
> = ({ roomId }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { data: messagesData, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.LIVECHAT_MESSAGES, 'preview', roomId],
            queryFn: () => getChatMessages({ roomId, limit: 50 }),
            enabled: !!roomId,
        },
        {
            loadingComponent: (
                <div className="flex justify-center items-center py-8">
                    <Loading.CircularProgress width={20} height={20} />
                </div>
            ),
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const messages = [...(messagesData?.messages || [])].reverse()

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white rounded-lg p-3 space-y-1">
            {messages.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                    <MessageCircle
                        size={32}
                        className="mx-auto mb-2 text-gray-300"
                    />
                    <p className="text-sm">{t('No messages yet')}</p>
                </div>
            ) : (
                <>
                    {messages.map((msg) => (
                        <PreviewMessage key={msg.id} message={msg} />
                    ))}
                </>
            )}

            {/* Link to full livechat room */}
            <div className="flex justify-center pt-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/livechat/${roomId}`)}
                >
                    <ExternalLink size={12} className="mr-1" />
                    {t('view-full-chat')}
                </Button>
            </div>
        </div>
    )
}

export default LivechatConversationPreview
